import React, { Component } from 'react';
import { Link } from "react-router-dom";
import SectionHeading from "../../components/SectionHeading/SectionHeading";
import CheckboxDropDown from "../../components/CheckboxDropDown/CheckboxDropDown";
import CreateNewLead from "./CreateNewLead/CreateNewLead";
import css from './Leads.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import {connect} from "react-redux";
import getDateQry from "../../helpers/dateHelpers";
import { DEFAULT_SELECTED_TIME_PERIOD, timePeriods } from "../../helpers/timePeriods";
import moment from 'moment';
import _ from 'lodash';


/**
 * @class
 *
 * Leads page.
 */
const Leads = class extends Component {
    // ---------- class fields: ----------
    source = axios.CancelToken.source();

    statusOptions = [
        'Active',
        'Inactive',
        'Open',
        'Dead',
        'Appointment Set',
        'Appointment Completed',
        'Appointment Rescheduled',
        'Presented Proposal',
        'Contact Attempted',
        'Working',
        'Did Not Close',
        'Qualified',
        'Unqualified',
        'Nurturing',
        'Super Dead',
        'Qualified Sit'
    ];

    // ---------- state: ----------
    state = {
        postData: {
            "limit": 500,
            "skip": 0,
            "fields": [
                "Id",
                "Name",
                "CreatedDate",
                "FirstName",
                "MiddleName",
                "LastName",
                "Phone",
                "Email",
                "Status",
                "Status__c",
                "Street",
                "City",
                "State",
                "PostalCode",
                "Country",
                "Sales_Representative__c",
                "Sales_Representative_2__c",
                "Sales_Representative_3__c",
                "Company",
                "ConvertedOpportunityId",
                "System_Size__c",
                "Prelim_Quote_ID__c",
                "Preferred_Type__c",
                "Preferred_Financier__c",
                "Preferred_Rate__c",
                "Preferred_Offset__c",
                "Preferred_Escalator__c",
                "Full_Deal_Requested__c",
                "Proposal_Notes__c",
                "Prelim_Appointment_Time__c",
                "Lead_Urgency__c",
                "Lead_Purpose__c",
                "Rate_Change__c",
                "Interest_Rate__c"
            ],
            "where": {
                "ConvertedOpportunityId": {
                    "$exists": 0
                },
                "Fake__c": false,
                "IsConverted": false,
                "RecordTypeId": {$ne: '012o0000000nzD2'},
                "$or": [
                    {
                        "Sales_Representative__c": this.props.user.apiId
                    },
                    {
                        "Sales_Representative_2__c": this.props.user.apiId
                    },
                    {
                        "Sales_Representative_3__c": this.props.user.apiId
                    }
                ]
            },
            "datetime": {
                ...getDateQry(DEFAULT_SELECTED_TIME_PERIOD, 'CreatedDate')
            }
        },
        statusDropdownOptions: [],
        selectedStatuses: [],
        numberDisplayedRecords: 0,
        optionsTimePeriod: timePeriods,
        selectedTimePeriod: '',
        shouldShowCreateNewLead: false,
        filterKeyword: '',
        tableData: [],
        showName: false,
        showCreatedDate: true,
        showStatus__c: false
    };

    // ---------- life cycle methods: ----------
    componentDidMount() {
        window.scrollTo(0, 0);
        
        // we need to initialize `statusDropdownOptions` and `selectedStatuses` lists given `statusOptions`:
        const statusDropdownOptions = this.statusOptions.map(option => {
            return {
                label: option,
                value: option,
                checked: true
            };
        });

        this.setState({
            selectedStatuses: this.statusOptions,
            statusDropdownOptions
        });

        // fetch data from Salesforce:
        this.fetchRecords();
    }

    componentWillUnmount() {
        this.source.cancel();
    }

    // ---------- methods: ----------
    /**
     * @method
     * @methodOf Leads
     *
     * Fetches leads records and sets data to state.
     */
    fetchRecords = () => {
        axios.post(
            '/sf/lead/find',
            this.state.postData,
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                const sortedData = _.orderBy(res.data.records, ['CreatedDate'], ['desc'])
                this.setState({ tableData: sortedData})
            })
            .catch(err => {
                if (axios.isCancel(err)) {
                    console.log('Request canceled');
                } else {
                    console.log('(POST) Error while fetching leads:\n', err);
                }
            });
    };

    /**
     * @method
     * @methodOf Leads
     *
     * Discerns whether to show the 'create new lead' view. Sets `shouldShowCreateNewLead` state.
     *
     * @param val {boolean}: value to hide/show the 'create new lead' view.
     */
    handleShouldShowNewLead = (val) => {
        this.setState({shouldShowCreateNewLead: val});
    };

    // TODO: this method is exact copy as in KPIs and Customers -- should reuse function from utils:
    /**
     * @method
     * @methodOf Leads
     *
     * Handler for filter input -- filters the Leads table by the "name" column in the table.
     *
     * Updates state: the user input (filter keyword) the displayed table data (filtered by user input).
     * N.b. input field/filtering is case-insensitive.
     */
    handleFilterOnChange = (event) => {
        const filterKeyword = event.target.value.toLowerCase();

        this.setState({
            filterKeyword
        });
    };

    /**
     * @method
     * @methodOf Leads
     *
     * Handler for when user selects a time period from dropdown -- fetches data based on the selected time period.
     *
     * @param val {string}: The time period string.
     */
    timePeriodDropdownOnChange = (val) => {
        // select time period object from list based on selected drop down value:
        const selectedTimePeriod = timePeriods.find(item => item.value === val);

        // update object for POST request with selected time period:
        const postData = {...this.state.postData};
        postData.datetime = {...getDateQry(selectedTimePeriod, 'CreatedDate')};

        // fetch data after updating post data:
        this.setState({
                postData
            },
            this.fetchRecords);
    };

    /**
     * @method
     * @methodOf Leads
     *
     * The displayed table after all filters and sorting have been applied
     *
     * @returns {array}: The same `tableData` except filtered by keyword and selected statuses.
     */
    displayedTable = () => {
        let tableCheck = this.state.tableData;

        tableCheck.forEach(lead => {
            if(lead.Status__c === null){
                lead.Status__c = 'Inactive';
            }
        });

        // filter table by filter keyword:
        return this.state.tableData
            .filter(item => {
                const itemName = item.Name.toLowerCase();
                return itemName.includes(this.state.filterKeyword);
            })
            // filter by selected statuses (drop down):
            .filter(item => this.state.selectedStatuses.includes(item.Status__c));
    };

    tableSort (value) {
        let sortedData = '';
        let show = 'show' + value;
        let showName = false;
        let showCreatedDate = false;
        let showStatus__c = false;

        switch (show) {
            case 'showName' :
                showName = true;
            break;

            case 'showCreatedDate' : 
                showCreatedDate = true;
            break;

            case 'showStatus__c' :
                showStatus__c = true;
            break;

            default:
            break;
        }

        if(value === 'CreatedDate') {
            sortedData = _.orderBy(this.state.tableData, [value], ['desc']);
        } else {
            sortedData = _.orderBy(this.state.tableData, [value], ['asc']);
        }
        this.setState({ 
            tableData: sortedData,
            showName: showName,
            showCreatedDate: showCreatedDate,
            showStatus__c: showStatus__c
        })
    }

    /**
     * @method
     * @methodOf Leads
     *
     * Handler when user makes a selection on the CheckboxDropDown component to filter by status -- updates the selected statuses.
     *
     * @param optionList {array}: Array of objects that's equal to the `statusDropdownOptions` state variable we pass to the CheckboxDropDown.
     */
    filterByStatusOnChange = (optionList) => {
        const selectedStatuses = optionList
            .filter(item => item.checked)
            .map(item => item.value);

        this.setState({
            statusDropdownOptions: optionList,
            selectedStatuses,
        });
    };

    render() {
        const viewLeads = <>
            <div className="row">
                <div className="col-sm-12">
                    <h4>When</h4>
                </div>
            </div>

            {/* ---------- page controls row 1: ---------- */}
            <div className="row">
                <div className="col-7 col-sm-6 col-lg-3">
                    <select id="timePeriod" onChange={(event) => this.timePeriodDropdownOnChange(event.target.value)} defaultValue={DEFAULT_SELECTED_TIME_PERIOD.value}>
                        {
                            // ----- select options - time period: -----
                            this.state.optionsTimePeriod.map((option, index) => <option key={index} value={option.value}>{option.label}</option>)
                        }
                    </select>
                </div>
                <div className="col-5 col-sm-6 col-lg-9">
                    <button className="btn btn-primary" type="button" onClick={() => this.setState({shouldShowCreateNewLead: true})}>Create New Lead</button>
                </div>
            </div>

            {/* ---------- page controls row 2: ---------- */}
            <div className="row pt-4">
                <div className="col-sm-12 col-md-4 col-lg-3">
                    {/* ----- filter table data: ----- */}
                    <div className="input-with-icons-container">
                        <FontAwesomeIcon icon={faFilter} />
                        <input className="input"
                               type="text"
                               placeholder="Filter Results"
                               value={this.state.filterKeyword}
                               onChange={(event) => this.handleFilterOnChange(event)}
                        />
                    </div>
                </div>
                <div className="col-6 col-md-4 col-lg-3 mt-4 mt-md-0">
                    <CheckboxDropDown
                        button-outline
                        options={this.state.statusDropdownOptions}
                        onDropdownSelect={(options) => this.filterByStatusOnChange(options)}
                        text="Status"
                    />
                </div>
                {/* ----- show # of results: ----- */}
                <div className="col-6 col-md-4 col-lg-6 mt-4 mt-md-0 d-flex align-items-center justify-content-end">
                    Showing { this.displayedTable().length } of { this.state.tableData.length }
                </div>
            </div>

            {
                window.innerWidth <= 760 ? 

                <div>
                    {/* ---------- table: ---------- */}
                    <table class="table table-sm table-borderless">
                        <thead>
                        <tr>
                            <th className={css.Leads__Pointer} onClick={() => this.tableSort('Name')}>Name {this.state.showName ? <FontAwesomeIcon icon={faCaretDown}/> : ''}</th>
                            <th className={css.Leads__Pointer}  onClick={() => this.tableSort('CreatedDate')}>Created {this.state.showCreatedDate ? <FontAwesomeIcon icon={faCaretDown}/> : ''}</th>
                            <th className={css.Leads__Pointer}  onClick={() => this.tableSort('Status__c')}>Status {this.state.showStatus__c ? <FontAwesomeIcon icon={faCaretDown}/> : ''}</th>
                        </tr>
                        </thead>

                        <tbody>
                        {
                            // ---------- table rows: ----------
                            this.displayedTable()
                                .map((lead) => {
                                    return (
                                        <tr key={ lead.Id }>
                                            <td><Link to={ `/leads/${lead.Id}` }>{lead.Name}</Link></td>
                                            <td>{ moment(lead.CreatedDate).format('MMM D, YYYY') }</td>
                                            <td>{ lead.Status__c }</td>
                                        </tr>
                                    );
                                })
                        }
                        </tbody>
                    </table>

                </div>

                :

                <div>
                    {/* ---------- table: ---------- */}
                    <table>
                        <thead>
                        <tr>
                            <th className={css.Leads__Pointer} onClick={() => this.tableSort('Name')}>Name {this.state.showName ? <FontAwesomeIcon icon={faCaretDown}/> : ''}</th>
                            <th>Phone</th>
                            <th className={css.Leads__Pointer}  onClick={() => this.tableSort('CreatedDate')}>Created {this.state.showCreatedDate ? <FontAwesomeIcon icon={faCaretDown}/> : ''}</th>
                            <th className={css.Leads__Pointer}  onClick={() => this.tableSort('Status__c')}>Status {this.state.showStatus__c ? <FontAwesomeIcon icon={faCaretDown}/> : ''}</th>
                            <th>KW</th>
                        </tr>
                        </thead>

                        <tbody>
                        {
                            // ---------- table rows: ----------
                            this.displayedTable()
                                .map((lead) => {
                                    return (
                                        <tr key={ lead.Id }>
                                            <td><Link to={ `/leads/${lead.Id}` }>{lead.Name}</Link></td>
                                            <td><a href={ `tel:${lead.Phone}` }>{lead.Phone}</a></td>
                                            <td>{ moment(lead.CreatedDate).format('MMM D, YYYY') }</td>
                                            <td>{ lead.Status__c }</td>
                                            <td><h3 className="m-0">{lead.System_Size__c ? lead.System_Size__c.toFixed(1) : '0.0' }</h3></td>
                                        </tr>
                                    );
                                })
                        }
                        </tbody>
                    </table>
                    
                </div>
            }

        </>;

        return (
            <div className={css.Leads}>
                <SectionHeading heading={this.state.shouldShowCreateNewLead ? 'New Lead' : 'Leads'} />

                { this.state.shouldShowCreateNewLead ? <CreateNewLead onShouldShowNewLead={(val) => this.handleShouldShowNewLead(val)} createLeadOnSuccess={() => this.fetchRecords()} /> : viewLeads }
            </div>
        );
    }
};


// ---------- Redux mapping: ----------
const mapStateToProps = state => {
    return {
        user: state.user
    };
};

export default connect(mapStateToProps)(Leads);
